import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'
import { HDSTETH_DEVICE_ID, subscribeHDStethBatches } from '@/hooks/useHDStethDevice'
import { useHDStethEcgValue } from '@/hooks/useHDStethEcgValue'
import type { HDStethEcgSnapshot, HDStethEcgSource } from '@/utils/hdSteth/ecg-types'
import { createEcgStore } from '@/utils/hdSteth/ecg-store'
import { HDStethEcgReceiver, HDStethEcgSender, HDStethEcgTransport } from '@/utils/hdSteth/ecg-stream'

/** How often an idle stream is checked for expiry. */
const EXPIRY_CHECK_MS = 250

const EcgContext = createContext<HDStethEcgSource | undefined>(undefined)
interface EcgProviderProps {
  enabled: boolean
  local: boolean
  selectedDeviceId?: string | null
  transport?: HDStethEcgTransport
}

/**
 * Own ECG acquisition independently of whether either viewer has expanded the panel.
 * Packets update an external store, never React state, so the stethoscope card does not re-render per packet.
 */
export function HDStethEcgProvider({ children, enabled, local, selectedDeviceId, transport }: PropsWithChildren<EcgProviderProps>): JSX.Element {
  const [store] = useState(createEcgStore)
  const isActive = enabled && (!local || selectedDeviceId === HDSTETH_DEVICE_ID)

  useEffect(() => {
    store.set(undefined)
    if (!isActive) return
    const receiver = new HDStethEcgReceiver()
    const receive = (message: unknown): void => store.set(message === null ? receiver.clear() : receiver.receive(message, Date.now()))
    const timer = window.setInterval(() => store.set(receiver.current(Date.now())), EXPIRY_CHECK_MS)
    const unsubscribe = local ? subscribeLocalEcg(receive, transport) : transport?.subscribe(receive)
    return () => {
      window.clearInterval(timer)
      unsubscribe?.()
      store.set(undefined)
    }
  }, [isActive, local, store, transport])

  return <EcgContext.Provider value={isActive ? store : undefined}>{children}</EcgContext.Provider>
}

function subscribeLocalEcg(receive: (message: unknown) => void, transport?: HDStethEcgTransport): () => void {
  let sender: HDStethEcgSender | null = null
  const stop = (): void => {
    if (sender) transport?.send(sender.stop())
    sender = null
    receive(null)
  }
  const unsubscribe = subscribeHDStethBatches((batch) => {
    if (!batch) { stop(); return }
    sender ??= new HDStethEcgSender()
    sender.packets(batch).forEach((message) => { receive(message); transport?.send(message) })
  })
  return () => { unsubscribe(); stop() }
}

const hasSnapshot = (snapshot: HDStethEcgSnapshot | undefined): boolean => snapshot !== undefined

/**
 * Returns the ECG source while a selected local or trusted remote HD Steth streams.
 * Re-renders only when the ECG appears or disappears, not on each packet.
 */
export function useHDStethEcg(): HDStethEcgSource | undefined {
  const source = useContext(EcgContext)
  const isPresent = useHDStethEcgValue(source, hasSnapshot)
  return isPresent ? source : undefined
}
